import { Link } from "react-router-dom";
import { useEffect, useState, useRef } from "react";
import axios from "axios";
import { Rocket, Moon, Users, DollarSign, Target, Check, ArrowLeft } from "lucide-react";
import { useLang } from "@/lib/lang";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import NavBar from "@/components/NavBar";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function MoonExplorer() {
  const { lang } = useLang();
  const T = lang === "fr" ? FR : EN;
  const [config, setConfig] = useState(null);
  const [progress, setProgress] = useState(null);
  const [selectedTier, setSelectedTier] = useState(null);
  const [form, setForm] = useState({ email: "", full_name: "" });
  const [showMap, setShowMap] = useState(false);
  const [paypalReady, setPaypalReady] = useState(false);
  const [creating, setCreating] = useState(false);
  const btnRef = useRef(null);

  const load = () => {
    axios.get(`${API}/moon/config`).then(r => setConfig(r.data));
    axios.get(`${API}/moon/progress`).then(r => setProgress(r.data));
  };
  useEffect(() => { load(); }, []);

  // Load PayPal SDK USD
  useEffect(() => {
    if (!config?.paypal_client_id) return;
    if (document.getElementById("paypal-sdk-moon")) { setPaypalReady(true); return; }
    const s = document.createElement("script");
    s.id = "paypal-sdk-moon";
    s.src = `https://www.paypal.com/sdk/js?client-id=${config.paypal_client_id}&currency=USD&intent=capture`;
    s.onload = () => setPaypalReady(true);
    document.body.appendChild(s);
  }, [config]);

  // Render PayPal button in modal when tier selected
  useEffect(() => {
    if (!selectedTier || !paypalReady || !window.paypal || !btnRef.current) return;
    btnRef.current.innerHTML = "";
    window.paypal.Buttons({
      style: { layout: "vertical", color: "gold", shape: "pill", label: "paypal", height: 45 },
      onClick: (data, actions) => {
        if (!form.email || !form.full_name || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
          toast.error(T.fillAll); return actions.reject();
        }
        return actions.resolve();
      },
      createOrder: async () => {
        setCreating(true);
        try {
          const r = await axios.post(`${API}/moon/reservations/create-order`, { tier_id: selectedTier, ...form }, { withCredentials: true });
          setCreating(false);
          return r.data.paypal_order_id;
        } catch (e) { setCreating(false); toast.error(e?.response?.data?.detail || "Error"); throw e; }
      },
      onApprove: async (data) => {
        try {
          const r = await axios.post(`${API}/moon/reservations/${data.orderID}/capture`);
          if (r.data.status === "paid") {
            toast.success(`🚀 ${T.reserved} #${r.data.seat_number}`, { duration: 5000 });
            setSelectedTier(null); setForm({ email: "", full_name: "" });
            load();
          } else toast.error("Paiement non complété");
        } catch (e) { toast.error("Erreur de capture"); }
      },
      onError: (err) => { console.error(err); toast.error("Erreur PayPal"); },
      onCancel: () => toast(T.cancelled),
    }).render(btnRef.current);
  }, [selectedTier, paypalReady, form, T]);

  return (
    <div className="min-h-screen text-white overflow-x-hidden" style={{
      background: "radial-gradient(circle at 20% 20%, rgba(88,28,135,0.5), transparent 50%), radial-gradient(circle at 80% 60%, rgba(30,64,175,0.4), transparent 55%), #050510",
    }} data-testid="moon-explorer-page">
      <NavBar variant="dark" />

      {/* Hero */}
      <section className="max-w-6xl mx-auto px-6 md:px-12 py-16 md:py-24 relative">
        <div className="absolute inset-0 opacity-30 pointer-events-none"
          style={{ backgroundImage: "radial-gradient(2px 2px at 20% 30%, white, transparent), radial-gradient(1px 1px at 60% 40%, white, transparent), radial-gradient(1px 1px at 80% 70%, white, transparent), radial-gradient(2px 2px at 40% 80%, white, transparent), radial-gradient(1px 1px at 15% 90%, white, transparent)" }} />
        <Link to="/store" className="text-white/60 text-sm flex items-center gap-1 mb-6 relative"><ArrowLeft size={14} /> {T.back}</Link>
        <div className="flex items-center gap-3 mb-4 text-purple-300 relative">
          <div className="h-px w-12 bg-purple-400/40" />
          <span className="font-mono-data text-[11px] uppercase tracking-[0.3em]">CosmoReach</span>
        </div>
        <h1 className="font-serif-mystery text-5xl md:text-7xl mb-4 relative" data-testid="moon-title">
          🌙 Moon Explorer<br />
          <span className="italic text-purple-300 text-3xl md:text-4xl">{T.subtitle}</span>
        </h1>
        <p className="text-lg text-white/70 max-w-2xl mb-8 relative">« {T.tagline} »</p>
        <a href="#tiers" className="relative inline-block">
          <Button size="lg" className="bg-purple-600 hover:bg-purple-700 text-white rounded-full h-14 px-8 text-lg shadow-2xl">
            <Rocket size={20} className="mr-2" /> {T.reserveBtn}
          </Button>
        </a>
      </section>

      {/* Progress counter */}
      {progress && (
        <section className="max-w-6xl mx-auto px-6 md:px-12 pb-12" data-testid="progress-section">
          <div className="rounded-3xl bg-white/5 border border-white/10 backdrop-blur p-8">
            <h2 className="font-serif-mystery text-3xl mb-2">🎯 {T.goal}</h2>
            <div className="grid md:grid-cols-4 gap-4 mt-6">
              <Stat icon={<DollarSign />} label={T.raised} value={`$${progress.raised.toLocaleString()}`} testId="stat-raised" />
              <Stat icon={<Target />} label={T.remaining} value={`$${progress.remaining.toLocaleString()}`} testId="stat-remaining" />
              <Stat icon={<Moon />} label={T.percent} value={`${progress.percentage}%`} testId="stat-percent" />
              <Stat icon={<Users />} label={T.participants} value={progress.participants.toLocaleString()} testId="stat-participants" />
            </div>
            <div className="mt-6">
              <div className="text-xs opacity-60 mb-1">$ {progress.raised.toLocaleString()} / $ {progress.goal.toLocaleString()}</div>
              <div className="h-3 rounded-full bg-white/10 overflow-hidden">
                <div className="h-full bg-gradient-to-r from-purple-500 via-pink-500 to-orange-400 transition-all duration-1000" style={{ width: `${Math.min(100, progress.percentage)}%` }} />
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Tiers */}
      <section id="tiers" className="max-w-6xl mx-auto px-6 md:px-12 pb-16">
        <h2 className="font-serif-mystery text-4xl mb-8">{T.chooseYourTier}</h2>
        {config && (
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            {config.tiers.map(t => {
              const p = progress?.by_tier?.[t.id];
              const filled = p ? Math.min(100, (p.reserved / p.target) * 100) : 0;
              return (
                <div key={t.id} data-testid={`tier-${t.id}`}
                  className="rounded-2xl border border-white/10 bg-white/5 backdrop-blur p-6 hover:border-purple-400 transition-all flex flex-col">
                  <div className="text-xs uppercase tracking-widest text-purple-300 mb-1">{lang === "fr" ? t.name_fr : t.name_en}</div>
                  <div className="font-serif-mystery text-4xl mb-2">${t.price}</div>
                  <p className="text-sm text-white/70 mb-4 flex-1">{lang === "fr" ? t.desc_fr : t.desc_en}</p>
                  <div className="text-xs text-white/50 mb-2">{p?.reserved || 0} / {t.target.toLocaleString()} {T.reserved}</div>
                  <div className="h-1.5 rounded-full bg-white/10 mb-4 overflow-hidden">
                    <div className="h-full bg-purple-500" style={{ width: `${filled}%` }} />
                  </div>
                  <Button data-testid={`select-tier-${t.id}`} onClick={() => setSelectedTier(t.id)}
                    className="w-full bg-purple-600 hover:bg-purple-700 rounded-full">{T.reserveTier}</Button>
                </div>
              );
            })}
          </div>
        )}
      </section>

      {/* Info */}
      <section className="max-w-6xl mx-auto px-6 md:px-12 pb-16 grid md:grid-cols-2 gap-6">
        <InfoCard title={T.philTitle} items={[T.phil1, T.phil2, T.phil3]} />
        <InfoCard title={T.practTitle} items={[T.pract1, T.pract2, T.pract3, T.pract4]} />
      </section>

      {/* Reservation modal */}
      {selectedTier && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur flex items-center justify-center p-4 z-50" data-testid="reserve-modal">
          <div className="bg-slate-900 border border-purple-500 rounded-2xl p-6 max-w-md w-full">
            <h3 className="font-serif-mystery text-2xl mb-4">🚀 {T.confirm} — ${TIER_PRICE[selectedTier]}</h3>
            <input data-testid="res-name" placeholder={T.fullName} value={form.full_name}
              onChange={e => setForm({ ...form, full_name: e.target.value })}
              className="w-full mb-3 bg-white/10 border border-white/20 rounded-lg h-11 px-3 text-white" />
            <input data-testid="res-email" placeholder={T.email} type="email" value={form.email}
              onChange={e => setForm({ ...form, email: e.target.value })}
              className="w-full mb-4 bg-white/10 border border-white/20 rounded-lg h-11 px-3 text-white" />
            {creating && <p className="text-xs text-white/60 mb-2">Création de la commande…</p>}
            <div ref={btnRef} data-testid="paypal-btn-container" className="mb-3" />
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setSelectedTier(null)} className="w-full border-white/20 text-white rounded-full">{T.cancel}</Button>
            </div>
            <p className="text-xs text-white/40 mt-3">{T.info}</p>
          </div>
        </div>
      )}

      {/* Seats map toggle */}
      <section className="max-w-6xl mx-auto px-6 md:px-12 pb-16">
        <Button data-testid="toggle-map-btn" onClick={() => setShowMap(!showMap)} className="bg-white/10 hover:bg-white/20 text-white rounded-full">
          {showMap ? T.hideMap : T.viewMap}
        </Button>
        {showMap && <SeatsMap />}
      </section>
    </div>
  );
}

const TIER_PRICE = { observer: 20, explorer: 200, pilot: 500, mission: 2000 };

function Stat({ icon, label, value, testId }) {
  return (
    <div className="text-center border border-white/10 rounded-xl p-4" data-testid={testId}>
      <div className="w-10 h-10 mx-auto rounded-full bg-purple-500/20 text-purple-300 flex items-center justify-center mb-2">{icon}</div>
      <div className="font-serif-mystery text-2xl">{value}</div>
      <div className="text-[10px] uppercase tracking-widest opacity-60">{label}</div>
    </div>
  );
}
function InfoCard({ title, items }) {
  return (
    <div className="rounded-2xl border border-white/10 bg-white/5 p-6">
      <h3 className="font-serif-mystery text-2xl mb-3 text-purple-300">{title}</h3>
      <ul className="space-y-2 text-sm text-white/80">
        {items.map((t, i) => <li key={i} className="flex items-start gap-2"><Check size={14} className="text-purple-400 mt-1" />{t}</li>)}
      </ul>
    </div>
  );
}
function SeatsMap() {
  const [rows, setRows] = useState([]);
  useEffect(() => { axios.get(`${API}/moon/reservations/public?limit=500`).then(r => setRows(r.data.reservations)); }, []);
  return (
    <div className="mt-4 rounded-2xl border border-white/10 bg-white/5 p-4" data-testid="seats-map">
      <div className="text-sm mb-3 opacity-70">{rows.length} places réservées</div>
      <div className="grid grid-cols-8 md:grid-cols-16 gap-1">
        {rows.slice(0, 200).map(r => (
          <div key={r.id} title={`${r.tier_name_fr} #${r.seat_number} — $${r.amount}`}
            data-testid={`seat-${r.id}`}
            className={`aspect-square rounded ${r.tier_id === "mission" ? "bg-pink-500" : r.tier_id === "pilot" ? "bg-orange-500" : r.tier_id === "explorer" ? "bg-purple-500" : "bg-blue-500"}`} />
        ))}
      </div>
    </div>
  );
}

const FR = {
  back: "Retour au store", subtitle: "L'espace enfin accessible à tous",
  tagline: "Découvrez, pilotez et explorez la Lune depuis votre écran !",
  reserveBtn: "🎟️ Réserver ma place", goal: "Objectif : 77 000 000 $",
  raised: "Collecté", remaining: "Restant", percent: "Atteint", participants: "Participants",
  chooseYourTier: "Choisis ton niveau", reserved: "réservées", reserveTier: "Réserver",
  philTitle: "🌍 Philosophie CosmoReach",
  phil1: "Moon Explorer est le premier projet de CosmoReach visant à rendre l'espace accessible à tous.",
  phil2: "Chaque participation contribue au développement du projet.",
  phil3: "CosmoReach veut rendre l'exploration spatiale plus abordable, interactive et ouverte au public.",
  practTitle: "🔒 Informations pratiques",
  pract1: "Paiement sécurisé.", pract2: "Option de remboursement intégral si la mission ne se fait pas.",
  pract3: "Accès aux livestreams HD.", pract4: "Réservation anticipée des sessions.",
  confirm: "Confirmer ta place", fullName: "Nom complet", email: "Courriel",
  confirmReserve: "Réserver maintenant", cancel: "Annuler",
  info: "Paiement sécurisé via PayPal Live. Ta place sera confirmée immédiatement après paiement.",
  fillAll: "Remplis nom + courriel valide", viewMap: "🗺️ Voir les places réservées", hideMap: "Masquer la carte",
  reserved: "Place réservée #",
  cancelled: "Paiement annulé",
};
const EN = {
  back: "Back to store", subtitle: "Space finally accessible to everyone",
  tagline: "Discover, pilot and explore the Moon from your screen!",
  reserveBtn: "🎟️ Reserve my seat", goal: "Goal: $77,000,000",
  raised: "Raised", remaining: "Remaining", percent: "Reached", participants: "Participants",
  chooseYourTier: "Choose your tier", reserved: "reserved", reserveTier: "Reserve",
  philTitle: "🌍 CosmoReach philosophy",
  phil1: "Moon Explorer is CosmoReach's first project aiming to make space accessible to all.",
  phil2: "Every participation helps fund the project.",
  phil3: "CosmoReach wants to make space exploration more affordable, interactive and public-facing.",
  practTitle: "🔒 Practical info",
  pract1: "Secure payment.", pract2: "Full refund option if the mission doesn't happen.",
  pract3: "HD livestream access.", pract4: "Early session booking.",
  confirm: "Confirm your seat", fullName: "Full name", email: "Email",
  confirmReserve: "Reserve now", cancel: "Cancel",
  info: "Secure PayPal Live checkout. Your seat is confirmed immediately upon payment.",
  fillAll: "Fill in name and valid email", viewMap: "🗺️ View reserved seats", hideMap: "Hide map",
  reserved: "Seat reserved #",
  cancelled: "Payment cancelled",
};
